/**
 * Column Formatters
 *
 * Cell rendering utilities for MetadataTable.
 * Uses displayFormatter from MetadataForm for value formatting.
 */

import React from 'react'
import { Tag } from 'antd'
import { ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, RollbackOutlined } from '@ant-design/icons'
import { formatValue } from '@/components/MetadataForm/utils/displayFormatter'
import type { FieldMetadata } from '@/services/metadataService'
import { formatJsonPreview, isJsonValue } from './jsonFormatters'

/**
 * Format a cell value based on field metadata.
 *
 * @param value - The raw value
 * @param metadata - Field metadata
 * @returns Formatted string for display
 */
export function formatCellValue(
  value: unknown,
  metadata: FieldMetadata
): string {
  // Handle null/undefined
  if (value == null) {
    return '-'
  }

  // Use display_format if specified
  if (metadata.display_format) {
    return formatValue(value, metadata.display_format)
  }

  // Format based on py_type
  const pyType = (metadata.py_type || 'string').toLowerCase()

  switch (pyType) {
    case 'bool':
    case 'boolean':
      return value ? 'Yes' : 'No'

    case 'timestamp':
    case 'timestamptz':
    case 'datetime':
      return formatValue(value, 'datetime')

    case 'date':
      return formatValue(value, 'date')

    case 'numeric':
    case 'decimal':
    case 'float':
      // Default to 2 decimal places for numeric
      return formatValue(value, 'number:2')

    case 'integer':
    case 'int':
      return formatValue(value, 'number:0')

    case 'uuid':
      // Show abbreviated UUID (first 8 chars)
      const uuidStr = String(value)
      return uuidStr.length > 8 ? `${uuidStr.substring(0, 8)}...` : uuidStr

    case 'json':
    case 'jsonb':
      // JSON objects/arrays get compact preview
      return formatJsonPreview(value)

    default:
      // Handle objects that weren't caught by type (e.g., untyped JSON columns)
      if (isJsonValue(value)) {
        return formatJsonPreview(value)
      }
      // Default string display
      return String(value)
  }
}

/**
 * Get CSS class for cell based on value and metadata.
 * Used for conditional styling (e.g., red for negative numbers).
 *
 * @param value - The raw value
 * @param metadata - Field metadata
 * @returns CSS class name or empty string
 */
export function getCellClassName(
  value: unknown,
  metadata: FieldMetadata
): string {
  const pyType = (metadata.py_type || 'string').toLowerCase()

  // Numeric fields - highlight negative values
  if (
    (pyType === 'numeric' ||
      pyType === 'decimal' ||
      pyType === 'float' ||
      pyType === 'integer' ||
      pyType === 'int') &&
    typeof value === 'number' &&
    value < 0
  ) {
    return 'metadata-table-cell-negative'
  }

  return ''
}

/**
 * Truncate long text for display with ellipsis.
 *
 * @param value - The string value
 * @param maxLength - Maximum length before truncation
 * @returns Truncated string with ellipsis if needed
 */
export function truncateText(value: string, maxLength: number = 50): string {
  if (value.length <= maxLength) {
    return value
  }
  return `${value.substring(0, maxLength)}...`
}

/**
 * Get status badge color based on common status values.
 *
 * @param status - Status string value
 * @returns Badge color for Ant Design
 */
export function getStatusBadgeColor(
  status: string
): 'success' | 'processing' | 'error' | 'warning' | 'default' {
  const lowerStatus = status.toLowerCase()

  // Success states
  if (
    lowerStatus === 'active' ||
    lowerStatus === 'approved' ||
    lowerStatus === 'completed' ||
    lowerStatus === 'success' ||
    lowerStatus === 'published'
  ) {
    return 'success'
  }

  // Processing/pending states
  if (
    lowerStatus === 'pending' ||
    lowerStatus === 'processing' ||
    lowerStatus === 'in_progress' ||
    lowerStatus === 'inprogress' ||
    lowerStatus === 'draft'
  ) {
    return 'processing'
  }

  // Error states
  if (
    lowerStatus === 'error' ||
    lowerStatus === 'failed' ||
    lowerStatus === 'rejected' ||
    lowerStatus === 'cancelled' ||
    lowerStatus === 'canceled'
  ) {
    return 'error'
  }

  // Warning states
  if (
    lowerStatus === 'warning' ||
    lowerStatus === 'expired' ||
    lowerStatus === 'archived' ||
    lowerStatus === 'inactive'
  ) {
    return 'warning'
  }

  return 'default'
}

/**
 * Check if field should render as a status badge.
 *
 * @param fieldKey - Field name
 * @param metadata - Field metadata
 * @returns True if field should render as badge
 */
export function isStatusField(fieldKey: string, metadata: FieldMetadata): boolean {
  const lowerKey = fieldKey.toLowerCase()

  // Check field name patterns
  if (
    lowerKey === 'status' ||
    lowerKey === 'state' ||
    lowerKey.endsWith('status') ||
    lowerKey.endsWith('state') ||
    lowerKey === 'lifecyclestate'
  ) {
    return true
  }

  // Check if it has allowed_values (enum-like)
  if (metadata.allowed_values && metadata.allowed_values.length <= 10) {
    return true
  }

  return false
}

/**
 * Convert string to Title Case (simple frontend formatting).
 *
 * @param str - String to convert
 * @returns Title cased string
 */
export function toTitleCase(str: string): string {
  return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : ''
}

/**
 * Check if field is lifecycle status (should render as colored badge).
 *
 * @param fieldKey - Field name
 * @param metadata - Field metadata
 * @returns True if field is lifecycle status
 */
export function isLifecycleField(fieldKey: string, metadata: FieldMetadata): boolean {
  const lowerKey = fieldKey.toLowerCase()

  // Explicit lifecycle field names
  if (
    lowerKey === 'lifecyclestate' ||
    lowerKey === 'lifecycle_state' ||
    lowerKey === 'lifecycle'
  ) {
    return true
  }

  // Check if allowed_values matches lifecycle pattern
  const allowedValues = metadata.allowed_values?.map(v => {
    const value = typeof v === 'string' ? v : v.value
    return value.toLowerCase()
  })
  if (
    allowedValues?.includes('draft') &&
    allowedValues?.includes('active')
  ) {
    return true
  }

  return false
}

/**
 * Render lifecycle status as colored Tag component.
 *
 * @param status - Raw status value (may be lowercase from backend)
 * @returns React element with Tag component
 */
export function renderLifecycleStatus(status: string): React.ReactNode {
  const lowerStatus = status?.toLowerCase()

  // Draft states
  if (lowerStatus === 'draft') {
    return (
      <Tag color="processing" icon={<ClockCircleOutlined />}>
        Draft
      </Tag>
    )
  }

  // Pending approval state
  if (lowerStatus === 'pending_approval') {
    return (
      <Tag color="warning" icon={<ExclamationCircleOutlined />}>
        Pending Approval
      </Tag>
    )
  }

  // Active states
  if (lowerStatus === 'active' || lowerStatus === 'published') {
    return (
      <Tag color="success" icon={<CheckCircleOutlined />}>
        Active
      </Tag>
    )
  }

  // Expired/inactive states
  if (lowerStatus === 'expired' || lowerStatus === 'inactive') {
    return (
      <Tag color="default" icon={<CloseCircleOutlined />}>
        Expired
      </Tag>
    )
  }

  // Superseded state
  if (lowerStatus === 'superseded') {
    return (
      <Tag color="default" icon={<RollbackOutlined />}>
        Superseded
      </Tag>
    )
  }

  // Fallback for unknown statuses
  return <Tag>{toTitleCase(status)}</Tag>
}
